import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaHeart,
  FaSearch,
} from "react-icons/fa";
import {
  getCustomerWishlist,
  removeSavedCoupon,
} from "../../services/wishlistService";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import "./Wishlist.css";
import blockClubLogo from "../../media/BC.png";
import { capitalizeCategory, getCategoryIcon } from "../../components/VenueCard";
// COMMENTED OUT - Search and category filter disabled
// import SearchAndCategoryFilter from "../../components/SearchAndCategoryFilter";
import "../VenueDetail/VenueDetail.css"; // reuse ticket styles from VenueDetail page
import "../../components/VenueCouponsSection/VenueCouponsSection.css"; // modal styles
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import { trackCouponRedeemAttempt, trackCouponRedeemSuccess, trackCouponRedeemFail } from "../../utils/analytics";
import WebCodeModals from "../../components/WebCodeModals/WebCodeModals";
import { CouponCard } from "../../components/CouponCard/CouponCard";

const ALL_TAB = "ALL";

const Wishlist = () => {
  const { customerToken: tokenFromParams } = useParams();
  const navigate = useNavigate();
  const { viewMode, customerToken: tokenFromAuth, setShowLoading, updateWishlistCount } = useAuth();

  // Use token from auth context if available, otherwise from URL params
  const customerToken = tokenFromAuth || tokenFromParams;

  // data & status
  const [wishlistData, setWishlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  // Track in-flight redemption requests to prevent double-redeem
  const [redeemingIds, setRedeemingIds] = useState(new Set());

  // Check if user is in public mode
  const isPublicMode = viewMode === "public" || customerToken === "public";
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  // metadata
  const [venueCategories, setVenueCategories] = useState({});
  const [categoryIconMap, setCategoryIconMap] = useState({});
  const [venueRank, setVenueRank] = useState({});
  const [apiCategoryOrder, setApiCategoryOrder] = useState([]);

  // logos
  const [venueLogos, setVenueLogos] = useState({});

  // UI state
  const [collapsedCats, setCollapsedCats] = useState({});
  const toggleCat = (cat) =>
    setCollapsedCats((p) => ({ ...p, [cat]: !p[cat] }));

  // COMMENTED OUT - Search and category filter disabled
  // search
  // const [searchQuery, setSearchQuery] = useState("");
  // const [isSearchFocused, setIsSearchFocused] = useState(false);

  // NEW: which category tab is currently selected in the row
  // const [activeCategoryTab, setActiveCategoryTab] = useState(ALL_TAB);
  // const [isCategoryExpandedMobile, setIsCategoryExpandedMobile] =
  //   useState(true);

  // Hardcoded values for disabled search/filter - always show all coupons
  const searchQuery = "";
  const activeCategoryTab = ALL_TAB;

  // confirmation modals - removed, now directly removes without confirmation

  // redeem modal state
  const [redeemConfirmOpen, setRedeemConfirmOpen] = useState(false);
  const [pendingRedeemCoupon, setPendingRedeemCoupon] = useState(null);

  // Second modal for web_code coupons (shows discount code after redemption)
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [redeemedWebCodeCoupon, setRedeemedWebCodeCoupon] = useState(null);

  // toast notification
  const [toast, setToast] = useState(null);

  /* -------------------- Effects -------------------- */

  // Control header/footer visibility when showing loading page
  useEffect(() => {
    setShowLoading(loading);
  }, [loading, setShowLoading]);

  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerToken]);

  useEffect(() => {
    const fetchVenueMeta = async () => {
      if (!customerToken) return;
      try {
        const res = await fetch(
          `${apiUrl}/loyalty/enrolled-venues/personalized?customer_token=${encodeURIComponent(
            customerToken
          )}`
        );
        if (!res.ok) throw new Error("Failed to fetch venue metadata");
        const data = await res.json();

        const catMap = {};
        const iconMap = {};
        const rankMap = {};
        const logoMap = {};
        (data?.companies || []).forEach((v, idx) => {
          const category = v.category || "Other";
          catMap[v.venue_id] = category;
          // Build category icon map from backend data
          if (!iconMap[category] && v.category_icon) {
            iconMap[category] = v.category_icon;
          }
          rankMap[v.venue_id] = idx;
          logoMap[v.venue_id] =
            v.venue_logo_url ||
            v.logo_url ||
            v.logo ||
            v.venue_logo ||
            v.image_url ||
            null;
        });

        setVenueCategories(catMap);
        setCategoryIconMap(iconMap);
        setVenueRank(rankMap);
        setVenueLogos((prev) => ({ ...prev, ...logoMap }));
        setApiCategoryOrder(
          (data?.category_order || []).map((c) => c.category)
        );
      } catch (e) {
        console.warn("Could not load venue metadata:", e);
        setVenueCategories({});
        setCategoryIconMap({});
        setVenueRank({});
        setApiCategoryOrder([]);
      }
    };
    fetchVenueMeta();
  }, [customerToken, apiUrl]);

  useEffect(() => {
    const fillMissingLogos = async () => {
      const saved = wishlistData?.saved_coupons || [];
      if (!saved.length) return;

      const uniqueVenueIds = [
        ...new Set(saved.map((c) => c.venue_id).filter(Boolean)),
      ];
      const missing = uniqueVenueIds.filter((id) => !venueLogos[id]);
      if (!missing.length) return;

      const entries = await Promise.all(
        missing.map(async (venueId) => {
          try {
            const res = await fetch(
              `${apiUrl}/coupon/venue/${venueId}/redeemable-coupons`
            );
            if (!res.ok) return [venueId, null];
            const data = await res.json();
            return [venueId, data?.venue_logo_url || null];
          } catch {
            return [venueId, null];
          }
        })
      );

      const add = {};
      entries.forEach(([vid, url]) => {
        if (url) add[vid] = url;
      });
      if (Object.keys(add).length) {
        setVenueLogos((prev) => ({ ...prev, ...add }));
      }
    };

    fillMissingLogos();
  }, [wishlistData, apiUrl, venueLogos]);

  // focus primary action when modal opens - removed since no confirmation modal

  /* -------------------- Helper functions -------------------- */

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const doRemove = useCallback(async (savedId) => {
    try {
      setRemovingId(savedId);
      await removeSavedCoupon(savedId, customerToken);
      setWishlistData((prev) => {
        if (!prev) return prev;
        const updatedCoupons = prev.saved_coupons.filter(
          (coupon) => coupon.saved_id !== savedId
        );
        const newCount = updatedCoupons.length;
        updateWishlistCount(newCount);
        return {
          ...prev,
          saved_coupons: updatedCoupons,
          total_count: newCount,
        };
      });
      showToast("Removed from wishlist", "info");
    } catch (err) {
      console.error("Error removing coupon:", err);
      showToast(err.message || "Failed to remove from wishlist", "error");
    } finally {
      setRemovingId(null);
    }
  }, [customerToken, updateWishlistCount]);

  // Keyboard shortcuts for modal - removed since no confirmation modal

  /* -------------------- Data -------------------- */

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomerWishlist(customerToken);

      // Normalize coupons - ensure is_active is set (default to true if undefined/null)
      const rawCoupons = data?.saved_coupons || [];

      // Debug logging for delivery coupons
      console.log('[Wishlist] Total coupons from backend:', rawCoupons.length);
      const deliveryCoupons = rawCoupons.filter(c => c.coupon_type === 'web_code');
      console.log('[Wishlist] Delivery coupons (web_code):', deliveryCoupons.length, deliveryCoupons);

      const cleanedCoupons = rawCoupons.map((c) => ({
        ...c,
        is_active: c.is_active !== undefined && c.is_active !== null ? c.is_active : true
      }));

      const newCount = cleanedCoupons.length;
      setWishlistData({
        ...data,
        saved_coupons: cleanedCoupons,
        total_count: newCount,
      });
      // Update global wishlist count to reflect filtered coupons
      updateWishlistCount(newCount);
    } catch (err) {
      console.error("Error fetching wishlist:", err);
      setError(err.message || "Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Remove flow -------------------- */
  // Removed askRemoveCoupon - now directly calls doRemove without confirmation

  /* -------------------- Redeem flow -------------------- */

  const openRedeemConfirm = (coupon) => {
    // If user is in public mode, redirect to login
    if (isPublicMode) {
      navigate(appendCompanyCode("/perks-directory"));
      return;
    }
    setPendingRedeemCoupon(coupon);
    setRedeemConfirmOpen(true);
  };

  const closeRedeemConfirm = () => {
    setRedeemConfirmOpen(false);
    setPendingRedeemCoupon(null);
  };

  const closeCodeModal = () => {
    setShowCodeModal(false);
    setRedeemedWebCodeCoupon(null);
  };

  const confirmRedeem = async () => {
    if (!pendingRedeemCoupon) return;

    const isWebCode = pendingRedeemCoupon.coupon_type === 'web_code';
    closeRedeemConfirm();

    // Resolve the coupon_issue_id (wishlist items may only have coupon_id)
    let couponIssueId = pendingRedeemCoupon.coupon_issue_id;
    let fullCouponData = pendingRedeemCoupon;

    try {
      if (!couponIssueId && pendingRedeemCoupon.coupon_id) {
        const customerCouponsRes = await fetch(
          `${apiUrl}/coupon/customer/${encodeURIComponent(customerToken)}`
        );
        if (!customerCouponsRes.ok) throw new Error("Failed to fetch customer coupons");

        const customerData = await customerCouponsRes.json();
        const issuedCoupon = (customerData.coupons || []).find(
          (c) =>
            c.coupon_id === pendingRedeemCoupon.coupon_id &&
            c.status === "ready" &&
            new Date(c.expires_at) > new Date()
        );

        if (issuedCoupon) {
          couponIssueId = issuedCoupon.coupon_issue_id;
          fullCouponData = { ...pendingRedeemCoupon, coupon_issue_id: issuedCoupon.coupon_issue_id };
        } else {
          throw new Error("You don't have an active coupon to redeem. Please enroll in the venue's loyalty program first.");
        }
      }

      // Prevent double-redeem if a request for this coupon is already in flight
      if (redeemingIds.has(couponIssueId)) return;
      setRedeemingIds((prev) => new Set(prev).add(couponIssueId));

      const removeFromWishlist = () => {
        const couponIdentifier = fullCouponData.coupon_issue_id || fullCouponData.coupon_id;
        setWishlistData((prev) => {
          if (!prev) return prev;
          const updated = prev.saved_coupons.filter(
            (c) => (c.coupon_issue_id || c.coupon_id) !== couponIdentifier
          );
          updateWishlistCount(updated.length);
          return { ...prev, saved_coupons: updated, total_count: updated.length };
        });
      };

      try {
        trackCouponRedeemAttempt(couponIssueId, fullCouponData.venue_id, customerToken, 'wishlist');

        const res = await fetch(`${apiUrl}/coupon/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customer_token: customerToken, coupon_issue_id: couponIssueId }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // 409 = already redeemed (concurrent tap) — treat as success in the UI
          if (res.status === 409) {
            removeFromWishlist();
            showToast("Coupon already redeemed", "info");
            return;
          }
          throw new Error(err.detail || "Redemption failed");
        }

        removeFromWishlist();
        trackCouponRedeemSuccess(couponIssueId, fullCouponData.venue_id, customerToken, 'wishlist');

        if (isWebCode) {
          setRedeemedWebCodeCoupon(fullCouponData);
          setShowCodeModal(true);
        }

        showToast("Coupon redeemed successfully!", "success");
      } catch (e) {
        console.error("Redeem error:", e);
        trackCouponRedeemFail(couponIssueId, fullCouponData.venue_id, e.message, 'wishlist');
        showToast(e.message || "Redemption failed. Please try again.", "error");
      } finally {
        setRedeemingIds((prev) => {
          const next = new Set(prev);
          next.delete(couponIssueId);
          return next;
        });
      }
    } catch (e) {
      // Error resolving coupon_issue_id before we could start the request
      console.error("Redeem setup error:", e);
      trackCouponRedeemFail(
        pendingRedeemCoupon.coupon_issue_id || pendingRedeemCoupon.coupon_id,
        pendingRedeemCoupon.venue_id,
        e.message,
        'wishlist'
      );
      showToast(e.message || "Redemption failed. Please try again.", "error");
    }
  };

  /* -------------------- Helpers -------------------- */

  const handleGoToVenueCoupons = (venueId) => {
    if (!venueId) return;
    navigate(`/coupons/${customerToken}/venue/${venueId}`);
  };


  const getCouponCategory = (c) =>
    venueCategories[c.venue_id] ||
    c.venue_category ||
    c.category ||
    (c.venue && (c.venue.category || c.venue.category_name)) ||
    "Other";

  const groupByCategory = (coupons) =>
    coupons.reduce((acc, c) => {
      const cat = getCouponCategory(c);
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(c);
      return acc;
    }, {});

  const sortCouponsByVenueRank = (arr) =>
    [...arr].sort((a, b) => {
      const ra = Number.isInteger(venueRank[a.venue_id])
        ? venueRank[a.venue_id]
        : Number.MAX_SAFE_INTEGER;
      const rb = Number.isInteger(venueRank[b.venue_id])
        ? venueRank[b.venue_id]
        : Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      const sa = new Date(a.saved_at || 0).getTime();
      const sb = new Date(b.saved_at || 0).getTime();
      return sb - sa;
    });

  const matchesSearch = (c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.title && c.title.toLowerCase().includes(q)) ||
      (c.description && c.description.toLowerCase().includes(q)) ||
      (c.venue_name && c.venue_name.toLowerCase().includes(q)) ||
      getCouponCategory(c).toLowerCase().includes(q)
    );
  };


  /* ===========================================
     Loading & Error: still wrap in .wishlist-page
     so the background covers the full viewport
     =========================================== */

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="error-state">
            <p>{error}</p>
            <button className="btn-retry" onClick={fetchWishlist}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------- Derived data for main UI -------------------- */

  const allCoupons = wishlistData?.saved_coupons || [];

  // NOTE: search filter only, no category filter here
  // Saved coupons (offers) don't have expires_at anymore - they're controlled by is_active
  const activeCoupons =
    allCoupons.filter(
      (c) => c.is_active && matchesSearch(c)
    ) || [];

  const expiredCoupons =
    allCoupons.filter(
      (c) => !c.is_active && matchesSearch(c)
    ) || [];

  const activeByCategory = groupByCategory(activeCoupons);
  const expiredByCategory = groupByCategory(expiredCoupons);

  const computeOrderedCategories = (grouped) => {
    const present = Object.keys(grouped);
    if (apiCategoryOrder.length) {
      return apiCategoryOrder.filter((c) => present.includes(c));
    }
    return present.sort();
  };

  const activeCategoryOrder = computeOrderedCategories(activeByCategory);
  const expiredCategoryOrder = computeOrderedCategories(expiredByCategory);

  // pick a safe current tab (now WITH an "All" tab)
  const normalizedActiveTab =
    activeCategoryTab === ALL_TAB
      ? ALL_TAB
      : activeCategoryOrder.includes(activeCategoryTab)
      ? activeCategoryTab
      : ALL_TAB;

  // coupons to show for the currently selected tab
  const couponsForSelectedTab =
    normalizedActiveTab === ALL_TAB
      ? sortCouponsByVenueRank(activeCoupons) // all active coupons
      : sortCouponsByVenueRank(activeByCategory[normalizedActiveTab] || []);

  const getVenueLogo = (c) =>
    venueLogos[c.venue_id] ||
    c.venue_logo_url ||
    c.logo_url ||
    c.venue?.venue_logo_url ||
    c.venue?.logo_url ||
    wishlistData?.company_logo_url ||
    blockClubLogo;

  /* -------------------- Ticket renderer -------------------- */

  const renderWishlistTickets = (couponList, sectionTitle) => {
    if (couponList.length === 0) {
      return (
        <div className="coupon-section">
          {sectionTitle ? <h2 className="section-title">{sectionTitle}</h2> : null}
          <p className="empty">No coupons.</p>
        </div>
      );
    }

    return (
      <div className="coupon-section">
        {sectionTitle ? <h2 className="section-title">{sectionTitle}</h2> : null}
        <ul className="coupons-list wishlist-coupons-list">
          {couponList.map((c) => {
            // Saved coupons (offers) have expiration_days instead of expires_at
            const expiry = c.expiration_days
              ? `Expires ${c.expiration_days} days after claim`
              : null;
            const ready = c.is_active;

            const venueName =
              c.venue_name ||
              c.venue?.name ||
              c.venue?.venue_name ||
              c.venue?.location_name ||
              "";

            return (
              <li key={c.saved_id || `${c.venue_id}-${c.coupon_id || c.title}`}>
                <CouponCard
                  openConfirm={openRedeemConfirm}
                  coupon={c}
                  title={c.title}
                  description={c.description || ""}
                  expiration={expiry || "No Expiration"}
                  frequency={null}
                  redeemed={false}
                  isSaving={removingId === c.saved_id}
                  isSaved={true}
                  isReady={ready}
                  handleWishlistToggle={() => doRemove(c.saved_id)}
                  disabled={!ready && !isPublicMode}
                  requiresLogin={isPublicMode}
                  venueName={venueName}
                  onVenueNameClick={() => handleGoToVenueCoupons(c.venue_id)}
                  venueLogo={getVenueLogo(c)}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  /* -------------------- Modals (portal) -------------------- */

  // Remove confirmation modal - removed, now directly removes without confirmation

  // Redeem confirmation modal (matching VenueCouponsSection style)
  const redeemModal = redeemConfirmOpen && pendingRedeemCoupon
    ? createPortal(
        <div className="modal-backdrop redeem-confirmation-modal" onClick={closeRedeemConfirm}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              Redeem Coupon
            </h3>

            {pendingRedeemCoupon.coupon_type === 'web_code' ? (
              <>
                <div className="modal-warning-box">
                  <p className="modal-warning-text">
                    A discount code will be displayed for use on the third-party ordering website.
                  </p>
                </div>

                <div className="modal-character-container">
                  <div className="modal-character">💻</div>
                </div>

                <p className="modal-confirm-text">
                  Ready to redeem this coupon?
                </p>
              </>
            ) : (
              <>
                <div className="modal-warning-box">
                  <p className="modal-warning-text">
                    Show staff while redeeming.
                    <br />
                    Only counts if they see the <strong className="text-emphasis-red">FULL</strong> process.
                  </p>
                </div>

                <div className="modal-character-container">
                  <div className="modal-character">🤵</div>
                </div>

                <p className="modal-confirm-text">
                  Ready to redeem this coupon?
                </p>
              </>
            )}

            <div className="modal-button-group">
              <button
                onClick={closeRedeemConfirm}
                className="modal-btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmRedeem}
                className="modal-btn-confirm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  /* -------------------- Main render -------------------- */

  return (
    <div className="wishlist-page">
      <div className="wishlist-container">
        <div className="wishlist-header">
          <h1 className="wishlist-title">My Coupon Wallet</h1>
          <p className="wishlist-count">
            {wishlistData?.total_count || 0}{" "}
            {wishlistData?.total_count === 1
              ? "saved coupon ready to use"
              : "saved coupons ready to use"}
          </p>
        </div>

        {/* No Results Message */}
        {/* COMMENTED OUT - Search feature disabled for now
        {searchQuery.trim() && activeCategoryOrder.length === 0 && expiredCategoryOrder.length === 0 && (
          <div className="wishlist-section">
            <div className="wishlist-empty-state">
              <FaSearch className="wishlist-empty-state-icon" />
              <h2 className="wishlist-empty-state-title">
                No coupons found
              </h2>
              <p className="wishlist-empty-state-description">
                No coupons match "{searchQuery}". Try a different search term.
              </p>
            </div>
          </div>
        )}
        */}

        {/* Active */}
        {activeCategoryOrder.length > 0 && (
          <div className="wishlist-section">
            {/* Search and Category Filter */}
            {/* COMMENTED OUT - Search and category selection disabled for now
            <div className="wishlist-search-section">
              <SearchAndCategoryFilter
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isSearchFocused={isSearchFocused}
                setIsSearchFocused={setIsSearchFocused}
                categories={["All", ...activeCategoryOrder]}
                selectedCategory={normalizedActiveTab}
                setSelectedCategory={setActiveCategoryTab}
                isCategoryExpanded={isCategoryExpandedMobile}
                setIsCategoryExpanded={setIsCategoryExpandedMobile}
                showMobileToggle={true}
                searchPlaceholder="Search saved coupons by title, venue, category..."
                categoryCounts={{
                  All: activeCoupons.length,
                  ...Object.fromEntries(
                    activeCategoryOrder.map((cat) => [
                      cat,
                      (activeByCategory[cat] || []).length,
                    ])
                  ),
                }}
                categoryIconMap={categoryIconMap}
              />
            </div>
            */}

            {/* ---------- DESKTOP VIEW ---------- */}
            <div className="wishlist-active-desktop">
              {/* Flat list for the selected tab (desktop only) */}
              {renderWishlistTickets(couponsForSelectedTab, "", false)}
            </div>

            {/* ---------- MOBILE VIEW ---------- */}
            <div className="wishlist-active-mobile">

              {/* Grouped list per category */}
              <div className="wishlist-mobile-category-groups">
                {(
                  normalizedActiveTab === ALL_TAB
                    ? activeCategoryOrder
                    : activeCategoryOrder.includes(normalizedActiveTab)
                    ? [normalizedActiveTab]
                    : []
                ).map((cat) => {
                  const coupons = sortCouponsByVenueRank(
                    activeByCategory[cat] || []
                  );
                  if (!coupons.length) return null;

                  return (
                    <div
                      key={cat}
                      className="wishlist-mobile-category-block"
                    >
                      <div className="wishlist-mobile-cat-header">
                        {getCategoryIcon(cat)}
                        <h3>{capitalizeCategory(cat)}</h3>
                        <span className="wishlist-mobile-cat-count">
                          ({coupons.length})
                        </span>
                      </div>

                      <div className="wishlist-mobile-cat-coupons">
                        {renderWishlistTickets(coupons, "", false)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Expired stays grouped as before */}
        {expiredCategoryOrder.length > 0 && (
          <div className="wishlist-section">
            <h2 className="section-title">Expired / Inactive</h2>
            {expiredCategoryOrder.map((cat) => {
              const coupons = sortCouponsByVenueRank(
                expiredByCategory[cat]
              );
              return (
                <div key={`expired-${cat}`} className="wishlist-expired-category">
                  <div
                    onClick={() => toggleCat(cat)}
                    className="wishlist-category-header-wrapper"
                  >
                    {getCategoryIcon(cat)}
                    <h3 className="section-title wishlist-category-header-title">
                      {capitalizeCategory(cat)}{" "}
                      <span className="wishlist-category-count">
                        ({coupons.length})
                      </span>
                    </h3>
                    {collapsedCats[cat] ? <span>▼</span> : <span>▲</span>}
                  </div>

                  {!collapsedCats[cat] &&
                    renderWishlistTickets(coupons, "", true)}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {allCoupons.length === 0 && (
          <div className="empty-wishlist">
            <FaHeart className="empty-heart-icon" />
            <h2>Your coupon wallet is empty</h2>
            <p>Start saving coupons from venues you love to access them quickly.</p>
            <button
              className="btn-neutral"
              onClick={() => navigate(appendCompanyCode("/perks-directory"))}
            >
              <FaSearch />
              <span>Browse Coupons</span>
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {redeemModal}

      {/* Web Code Modal - Shows discount code for delivery coupons */}
      <WebCodeModals
        coupon={redeemedWebCodeCoupon}
        isOpen={showCodeModal}
        onClose={closeCodeModal}
        showToast={showToast}
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wishlist;
